import React, { memo, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import ClickableList from 'components/Shared/Layout/ClickableList';
import ExpandableSectionWithTitle from 'components/Shared/Layout/ExpandableSectionWithTitle';
import Item from 'components/LeftMenu/Rooms/Item/container';
import Placeholder from 'components/LeftMenu/Shared/Placeholder';
import CatchError from 'components/CatchError';
import UnsupportedBrowserComponent from 'components/LeftMenu/Rooms/UnsupportedBrowser';

import { isWebRtcCompatible } from 'utils/webRTC';
import isEqual from 'lodash/isEqual';
import { getUrl } from 'utils/url';
import useWhyDidYouUpdate from 'hooks/useWhyDidYouUpdate';
import ItemLoadings from '../Shared/ItemLoadings';

const formatMessages = intl => () => ({
    title: intl.formatMessage({
        id: 'menuLeft.rooms.title',
        defaultMessage: 'Conférences'
    }),
    placeholderTitle: intl.formatMessage({
        id: 'menuLeft.rooms.placeholder.title',
        defaultMessage: 'Vous n\'avez pas encore créé de conférence'
    }),
    expandGroup: intl.formatMessage({
        id: 'menuLeft.rooms.link.expand.group',
        defaultMessage: 'Voir les autres conférences'
    }),
    collapseGroup: intl.formatMessage({
        id: 'menuLeft.rooms.link.collapse.group',
        defaultMessage: 'Cacher les autres conférences'
    }),
    tooltipButtonPlus: intl.formatMessage({
        id: 'menuLeft.rooms.title.tooltip',
        defaultMessage: 'Créer une conférence'
    })
});

const ITEMS_DEFAULT_LENGTH = 3;

const Rooms = (props) => {
    const {
        getRooms,
        isLoading,
        _rooms = [],
        _currentFeed,
        rooms = [],
        intl
    } = props;
    const i18n = formatMessages(intl)();

    useWhyDidYouUpdate('components:colLeft:rooms', props);

    useEffect(getRooms, []);

    const content = useMemo(() => {
        if (isLoading) {
            return () => <ItemLoadings repeat={6} />;
        } 
        if (!isWebRtcCompatible()) {
            return () => (
                <CatchError>
                    <UnsupportedBrowserComponent />
                </CatchError>
            );
        } 
        if (_rooms.length) {
            return isExpanded => (
                <CatchError>
                    <ClickableList
                        items={_rooms && isExpanded ? _rooms : _rooms.slice(0, ITEMS_DEFAULT_LENGTH)}
                        _current={_currentFeed}
                        rooms={rooms}
                    >
                        {id => <Item id={id} />}
                    </ClickableList>
                </CatchError>
            );
        }
        return null;
    }, [isLoading, _rooms]);

    const placeholder = (
        <Placeholder
            title={i18n.placeholderTitle}
        />
    );

    return (
        <ExpandableSectionWithTitle
            addUrl={getUrl('roomCreate')}
            target="roomCreate"
            expandText={i18n.expandGroup}
            collapseText={i18n.collapseGroup}
            title={i18n.title}
            tooltip={i18n.tooltipButtonPlus}
            isLoading={isLoading}
            placeholder={placeholder}
            itemsDefaultLength={ITEMS_DEFAULT_LENGTH}
            itemsLength={_rooms.length}
            id="rooms"
        >
            {content}
        </ExpandableSectionWithTitle>
    );
}

Rooms.propTypes = {
    getRooms: PropTypes.func.isRequired,
    _currentFeed: PropTypes.string,
    rooms: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool.isRequired,
    _rooms: PropTypes.array,
    intl: PropTypes.object.isRequired
};

export default injectIntl(memo(Rooms, (prev, next) => prev.isLoading === next.isLoading && isEqual(prev._rooms, next._rooms)));
